import React, { useMemo, useState } from 'react';
import { Button } from 'antd';
import { useMixpanel } from 'react-mixpanel-browser';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient
} from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { CampaignItemDto, MemberDto } from '@api/Api';
import { toggleReachedEntityLimitModal } from '@redux/actions/modalAction';
import {
  findCampaignMembersToInvite,
  inviteMemberToCampaign
} from '@api/Campaign';
import { ActionToast } from '@components/Toasts';
import { useAuth } from '@hooks/useAuth';
import { MixpanelService, MixpanelEventType } from '@services/mixpanelService';
import InviteMembersSelect, {
  SelectedItem
} from '@components/InviteMembersSelect/InviteMembersSelect';
import InfiniteScroll from 'react-infinite-scroll-component';
import UserAvatar from '@components/UserAvatar';
import { useCurrentWorkspace } from '@hooks/workspace';
import { OrganizationRoleCopy, WorkspaceRoleCopy } from '@helpers/userRoles';
import { ReactComponent as CheckMarkSvg } from '@assets/icons/check-mark-blue.svg';

type InviteToCampaignStepProps = {
  onCloseModal: (campaignId: string) => void;
  campaign: CampaignItemDto;
};

export default function InviteToCampaignStep({
  onCloseModal,
  campaign
}: InviteToCampaignStepProps) {
  const dispatch = useDispatch();
  const mixpanel = useMixpanel();
  const [currentWorkspace] = useCurrentWorkspace(false);
  const { user } = useAuth(true, 'internal');

  const [selectedMembers, setSelectedMembers] = useState<SelectedItem[]>([]);

  const {
    data: members,
    hasNextPage: hasNextPageMembers,
    fetchNextPage: fetchNextPageMembers
  } = useInfiniteQuery({
    queryKey: ['campaign', 'members', 'find-to-invite', campaign.id],
    keepPreviousData: true,
    enabled: !!campaign.id,
    queryFn: async ({ pageParam, signal }) => {
      const { data } = await findCampaignMembersToInvite(
        { targetId: campaign.id, after: pageParam },
        { signal }
      );
      return data;
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasNext ? lastPage.endCursor : undefined
  });

  const membersList = useMemo(
    () => members?.pages.flatMap((x) => x.edges.map((x) => x.node)) ?? [],
    [members]
  );

  const queryClient = useQueryClient();
  const inviteMutation = useMutation({
    mutationFn: () =>
      inviteMemberToCampaign({
        campaignId: campaign.id,
        memberIdsOrEmails: selectedMembers
          .filter((x) => x.type === 'new' || x.type === 'member')
          .map((x) => (x.type === 'new' ? x.email : x.id))
      }),
    onSuccess: () => {
      toast(
        <ActionToast
          title={`Invitation${selectedMembers.length > 1 ? 's' : ''} sent!`}
          onUndo={undefined}
          description={undefined}
          closeToast={undefined}
        />,
        {
          hideProgressBar: true,
          style: { width: '345px', height: 80 },
          bodyClassName: 'toast_container--invitation'
        }
      );
      MixpanelService.track(
        mixpanel,
        user.id,
        MixpanelEventType.MANAGE_CAMPAIGNS,
        {
          campaignName: campaign.name,
          deleteCampaign: false,
          newCampaign: false,
          privateCampaign: campaign.isPrivate,
          sharedCampaign: !campaign.isPrivate,
          addTaskGroup: false,
          addContributor: true
        }
      );
      onCloseModal(campaign.id);
    },
    onError: (e: any) => {
      if (e?.response?.data?.errorCode === 'subscription_users_limit_error')
        dispatch(
          toggleReachedEntityLimitModal({
            visible: true,
            entity: 'contributor'
          })
        );
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries(['workspace', 'members']),
        queryClient.invalidateQueries(['campaign', 'members']),
        queryClient.invalidateQueries(['workspace', 'member-groups']),
        queryClient.invalidateQueries(['campaign', 'member-groups'])
      ]);
    }
  });

  const onToggleSelectMember = (member: MemberDto, isSelect: boolean) => {
    if (isSelect) {
      setSelectedMembers((prevSelectedMembers) =>
        prevSelectedMembers.filter((i) => {
          if (i.type === 'member') {
            return i.id !== member.id;
          }
          return true;
        })
      );
      return;
    }
    setSelectedMembers((prevSelectedMembers) => [
      ...prevSelectedMembers,
      {
        type: 'member',
        id: member.id,
        email: member?.user?.email || member?.invitationEmail || '',
        name: member.user?.name || ''
      }
    ]);
  };

  return (
    <div className="modal_container enitity-creation-modal campaign-folder-creation-modal-invite-container">
      <h2 className="modal_container__title" style={{ fontSize: '22px' }}>
        Invite to project
      </h2>
      <p className="modal_container__subtitle">
        Invite anyone to this project as a contributor. They will only have
        access to this project.
      </p>
      <div style={{ paddingBottom: 8 }}>Add contributors</div>
      <div className="members-select">
        <InviteMembersSelect
          value={selectedMembers}
          onChange={setSelectedMembers}
        />
      </div>
      <InfiniteScroll
        dataLength={membersList.length || 0}
        next={fetchNextPageMembers}
        hasMore={hasNextPageMembers || false}
        style={{
          overflowX: 'hidden',
          paddingRight: 16,
          marginRight: -16
        }}
        height="295px"
        loader={<></>}
      >
        <div className="members-title">
          Contributors
          <div className="members-count">{(membersList.length || 0) + 1}</div>
        </div>
        <div className="members-block" style={{ pointerEvents: 'none' }}>
          <div className="members-block-avatar">
            <UserAvatar
              isActive={true}
              size="large"
              src={user.pictureUrl || ''}
              userEmail={user.email}
              userName={user.name}
            />
          </div>
          <div className="members-block-info">
            <div className="members-block-name">{user.name}</div>
            <div className="members-block-text">
              {currentWorkspace?.organizationRole
                ? OrganizationRoleCopy[currentWorkspace?.organizationRole]
                : ''}
            </div>
          </div>
          <div className="members-block-text">You</div>
        </div>
        {membersList.map((item) => {
          const isSelected = selectedMembers.some(
            (i) => i.type === 'member' && i.id === item.id
          );
          const role =
            WorkspaceRoleCopy[item.workspaceRole || 'workspace://contributor'];
          const email = item.user?.email || item.invitationEmail || '';
          const name = item.user?.name || '';
          return (
            <div
              key={item.id}
              onClick={() => onToggleSelectMember(item, isSelected)}
              className="members-block"
            >
              <div className="members-block-avatar">
                <UserAvatar
                  isActive={!item.invitationEmail}
                  size="large"
                  src={item.user?.picture.url || ''}
                  userEmail={email}
                  userName={name}
                />
              </div>
              <div className="members-block-info">
                <div className="members-block-name">{name}</div>
                <div className="members-block-text">{role}</div>
              </div>
              {isSelected && (
                <div className="members-block-icon">
                  <CheckMarkSvg />
                </div>
              )}
            </div>
          );
        })}
      </InfiniteScroll>
      <div className="modal_container__actions" style={{ marginTop: 24 }}>
        <Button
          type="text"
          size="large"
          style={{
            marginRight: '16px',
            border: 'none',
            height: 48,
            padding: '8px 16px'
          }}
          onClick={() => onCloseModal(campaign.id)}
        >
          Skip
        </Button>
        <Button
          disabled={selectedMembers.length === 0}
          type="primary"
          size="large"
          className="submit_form"
          style={{ width: 196, height: 48, padding: '8px 12px' }}
          onClick={() => inviteMutation.mutate()}
          loading={inviteMutation.isLoading}
        >
          Invite to Project
        </Button>
      </div>
    </div>
  );
}
